<script setup>
import { withTrailingSlash } from '@/utils/siteUrl'

defineProps({
  heading:  { type: String, default: 'Our network' },
  intro:    { type: String, default: '' },
  // Optional label rendered directly above the logo grid (e.g. "Members").
  subtitle: { type: String, default: '' },
  // Optional line rendered directly beneath the logo grid, inside the band.
  note:     { type: String, default: '' },
  // Each item: { name, logo, url? } — `logo` is a public path e.g.
  // "/logos/placeholders/griffith-university.svg"; optional `url` turns the
  // tile into an external link to that organisation.
  items:    { type: Array,  default: () => [] },
  ctaLabel: { type: String, default: '' },
  ctaTo:    { type: String, default: '' },
})
</script>

<template>
  <section class="logos">
    <div class="inner">
      <div class="intro">
        <h2 class="title">{{ heading }}</h2>
        <p v-if="intro" class="lead">{{ intro }}</p>
      </div>

      <h3 v-if="subtitle" class="subtitle">{{ subtitle }}</h3>

      <ul class="grid">
        <li v-for="(item, i) in items" :key="`${item.name}-${i}`" class="tile">
          <!-- The link (or plain div when there's no url) IS the tile box —
               background/border/padding all live here so the whole block is
               interactable, not just the image inside it. -->
          <component
            :is="item.url ? 'a' : 'div'"
            class="tile-link"
            v-bind="item.url ? { href: item.url, target: '_blank', rel: 'noopener noreferrer' } : {}"
          >
            <img class="mark" :src="item.logo" :alt="item.name" loading="lazy" />
          </component>
        </li>
      </ul>

      <p v-if="note" class="note">{{ note }}</p>

      <RouterLink v-if="ctaLabel && ctaTo" :to="withTrailingSlash(ctaTo)" class="btn primary pill">
        <span class="label">{{ ctaLabel }}</span>
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path
            d="M5 12h14M13 6l6 6-6 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </RouterLink>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.logos {
  // Full-bleed cream band (matches .impact/.what-we-do): the background spans
  // the viewport so it reads as a clean horizontal break against the warm
  // resources band above; the .inner wrapper constrains content to the shared
  // centred 1200px column + gutter used by every other home section.
  background: var(--color-bg);
  padding-block: $section-space-block;
  @include from-md { padding-block: $section-space-block-md; }

  .inner {
    max-width: $container;
    margin-inline: auto;
    padding-inline: $gutter;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-6;
  }

  // Heading and blurb stack in a single flush-left column (blurb sits directly
  // below the heading, above the "Members" subtitle) rather than the former
  // two-column heading-beside-blurb split.
  .intro {
    display: flex;
    flex-direction: column;
    gap: $space-5;
    width: 100%;

    .title {
      margin: 0;
      color: var(--color-ink);
      line-height: $lh-heading;
      letter-spacing: $ls-heading;
    }

    .lead {
      margin: 0;
      color: var(--color-ink);
      font-size: $font-size-h4;
      line-height: 1.3;
    }
  }

  // Label above the logo grid (e.g. "Members"). Full width + left-aligned so
  // it reads as a section label, not a centred heading (the .inner centres its
  // children). Negative margin trims the .inner gap into a tighter label→grid
  // pairing.
  .subtitle {
    width: 100%;
    margin: 0 0 (-$space-3);
    color: var(--color-ink);
    font-size: $font-size-h4;
    line-height: $lh-heading;
  }

  .grid {
    display: grid;
    // jk-37 blanket rule: single column below $bp-xs (500px) — was a bare
    // 2-up grid at every width, cramming logo tiles two-per-row even on the
    // narrowest phones.
    grid-template-columns: 1fr;
    gap: $space-5;
    width: 100%;
    margin: 0;
    padding: 0;
    list-style: none;

    @include from-xs { grid-template-columns: repeat(2, 1fr); }
    @include from-md { grid-template-columns: repeat(4, 1fr); }

    .tile {
      .mark {
        max-width: 100%;
        max-height: 80px;
        width: auto;
        height: auto;
        object-fit: contain;
        // Sit slightly dimmed at rest, popping to full strength on hover/
        // focus — a subtle second cue alongside the tile's own lift.
        opacity: .9;
        @include transition(opacity);
      }

      // The link (or div, when there's no url) is the whole visual tile —
      // background/border/padding sit here so the entire block, not just the
      // image, is the clickable/interactive area.
      .tile-link {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 150px;
        padding: $space-6;
        background: var(--color-surface-warm);
        border: 1px solid var(--color-border-decorative);
        border-radius: $radius;
        text-decoration: none;
        color: inherit;
      }

      // Interactive states only apply when the tile is a real link — matches
      // the hover-nudge treatment used across cards (no opacity fade).
      a.tile-link {
        @include transition(box-shadow, background, transform);

        &:hover {
          background: var(--color-surface);
          box-shadow: 0 4px 6px rgba($color-ink, 0.16);
          transform: translateY(-2px);
        }
        &:focus-visible { @include focus-ring; }
        &:active { transform: translateY(1px); }

        &:hover .mark,
        &:focus-visible .mark { opacity: 1; }
      }
    }
  }

  // Short acknowledgement sitting directly under the logo grid (e.g. the
  // Queensland Department of Education project-partner note on Our Network).
  // Full width + left-aligned so it reads as a caption, not a centred blurb.
  .note {
    width: 100%;
    margin: 0;
    color: var(--color-ink);
    // Matches the Our Network partners body copy (base body size) rather than
    // standing out at $font-size-lg — the note's only consumer is that page's
    // Queensland Department of Education line (author request, 2026-08-09).
    font-size: $font-size-base;
    line-height: 1.3;
  }
}
</style>
